import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle2Icon, ChevronDown, ClockIcon } from 'lucide-react';
import { Step, StepStatus } from './SidebarFormNavigation';
import { observer } from 'mobx-react';
import ApplyViewModel from './ApplyViewModel';
import useAsyncEffect from 'use-async-effect';
import { useParams } from 'react-router-dom';
import { Appl } from '../../../../core/Appl';
import { SubmissionStatus } from '../OraEnums';
import { Steps } from './ApplyModels';

interface OverviewProps {
  retirementSteps: Step[];
}

const RetirementOverview: React.FC<OverviewProps> = observer(({ retirementSteps }) => {
  const vm = Appl.Services.get<ApplyViewModel>('ApplyViewModel');
  vm.navigate = useNavigate();
  const { memberId } = useParams();

  // Modified helper function to check if a step is fully complete
  const isStepFullyComplete = (step: Step): boolean => {
    // If there are substeps, ALL must be complete for the step to be considered complete
    if (step.subSteps?.length) {
      return step.subSteps.every(subStep => subStep.status === StepStatus.Completed);
    }

    // If no substeps, then just check the main step status
    return step.status === StepStatus.Completed;
  };

  // Helper function to find the first incomplete step/substep
  const findFirstIncompleteStep = (steps: Step[]): { stepIndex: number; subStepIndex: number | null } => {
    for (let stepIndex = 0; stepIndex < steps.length; stepIndex++) {
      const step = steps[stepIndex];

      // Check if the step is fully complete
      if (!isStepFullyComplete(step)) {
        // If there are substeps, find the first incomplete one
        if (step.subSteps?.length) {
          for (let subStepIndex = 0; subStepIndex < step.subSteps.length; subStepIndex++) {
            if (step.subSteps[subStepIndex].status !== StepStatus.Completed) {
              return { stepIndex, subStepIndex };
            }
          }
        }
        // If no substeps or couldn't find incomplete substep, return main step
        return { stepIndex, subStepIndex: null };
      }
    }
    return { stepIndex: -1, subStepIndex: null };
  };

  // Initialize expanded sections state
  const [expandedSections, setExpandedSections] = React.useState<Record<string, boolean>>(() => {
    const { stepIndex } = findFirstIncompleteStep(retirementSteps);
    return retirementSteps.reduce((acc, step, index) => ({
      ...acc,
      [step.slug]: index === stepIndex
    }), {});
  });

  useAsyncEffect(async () => {
    let isValid = await vm.validateReferrerUrl();
    if (isValid) {
      if (memberId) {
        await vm.getStepStatuses(memberId);
        let submission = await vm.getSubmissionByMemberIdAsync(memberId);
        if (submission.lovSubmissionStatusId && submission.lovSubmissionStatusId !== SubmissionStatus.Draft) {
          await vm.navigateToStepAsync(`${Steps.SubmitApplication}`)
        }
      }
    }

  }, [memberId]);

  async function validateRetirementDate(e: any): Promise<void> {

    let toDate: (string | Date) | null | undefined = vm.model.personalInformation.retirementEffDate;
    if (!toDate) {
      if (vm.submission?.retirementDate) {
        toDate = vm.submission.retirementDate;
      }
    }
    let dateCheck = await vm.doRetirementDateValidationAsync(toDate as Date, true);
    if (dateCheck !== "") {
      e.preventDefault();
      Appl.MessageBox.show(dateCheck, 'Retirement Date No Longer Valid');
      vm.submitApplicationValidationMessageTitile = `Attention: Retirement Date No Longer Valid`;
      vm.submitApplicationValidationMessage = dateCheck;
      if (memberId) {
        let submission = await vm.getSubmissionByMemberIdAsync(memberId);
        if (submission.appSelfReviewed === 'Y') {
          vm.navigatedFromReviewScreen = true;
        }
      }
      vm.navigateToStepAsync(`${Steps.SelectDate}`);
    }
  };

  const toggleSection = (slug: string): void => {
    setExpandedSections(prev => ({
      ...prev,
      [slug]: !prev[slug]
    }));
  };

  // Process steps to mark everything after first incomplete as NotStarted
  const processedSteps = React.useMemo(() => {
    const { stepIndex: firstIncompleteStepIndex, subStepIndex } = findFirstIncompleteStep(retirementSteps);

    return retirementSteps.map((step, i) => {
      // Create a copy of the step to modify
      const updatedStep = { ...step };

      if (i < firstIncompleteStepIndex) {
        // Steps before the first incomplete step remain completed
        updatedStep.status = StepStatus.Completed;
        if (updatedStep.subSteps) {
          updatedStep.subSteps = updatedStep.subSteps.map(sub => ({
            ...sub,
            status: StepStatus.Completed
          }));
        }
      } else if (i === firstIncompleteStepIndex) {
        // The first incomplete step is marked as active
        updatedStep.status = StepStatus.Active;
        if (updatedStep.subSteps) {
          updatedStep.subSteps = updatedStep.subSteps.map((sub, j) => ({
            ...sub,
            status: j < subStepIndex! ? StepStatus.Completed :
              j === subStepIndex ? StepStatus.Active :
                StepStatus.NotStarted
          }));
        }
      } else {
        // All steps after the first incomplete step are marked as not started
        updatedStep.status = StepStatus.NotStarted;
        if (updatedStep.subSteps) {
          updatedStep.subSteps = updatedStep.subSteps.map(sub => ({
            ...sub,
            status: StepStatus.NotStarted
          }));
        }
      }

      return updatedStep;
    });
  }, [retirementSteps]);

  // Update the getStatusIcon to reflect parent status based on substeps
  const getStatusIcon = (step: Step): JSX.Element => {
    // Determine the effective status
    let effectiveStatus = step.status;
    if (step.subSteps?.length) {
      if (!isStepFullyComplete(step)) {
        effectiveStatus = step.status === StepStatus.Active ?
          StepStatus.Active : StepStatus.NotStarted;
      }
    }

    switch (effectiveStatus) {
      case StepStatus.Completed:
        return <div className='h-6 w-6 flex items-center justify-center rounded-full'>
          <svg className='text-primary-500 w-full h-full' viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.5 2.15625C13.9781 2.15625 16.3547 3.14068 18.107 4.89297C19.8593 6.64526 20.8438 9.02188 20.8438 11.5C20.8438 13.9781 19.8593 16.3547 18.107 18.107C16.3547 19.8593 13.9781 20.8438 11.5 20.8438C9.02188 20.8438 6.64526 19.8593 4.89297 18.107C3.14068 16.3547 2.15625 13.9781 2.15625 11.5C2.15625 9.02188 3.14068 6.64526 4.89297 4.89297C6.64526 3.14068 9.02188 2.15625 11.5 2.15625ZM11.5 23C14.55 23 17.4751 21.7884 19.6317 19.6317C21.7884 17.4751 23 14.55 23 11.5C23 8.45001 21.7884 5.52494 19.6317 3.36827C17.4751 1.2116 14.55 0 11.5 0C8.45001 0 5.52494 1.2116 3.36827 3.36827C1.2116 5.52494 0 8.45001 0 11.5C0 14.55 1.2116 17.4751 3.36827 19.6317C5.52494 21.7884 8.45001 23 11.5 23ZM16.5762 9.38867C16.9984 8.96641 16.9984 8.28359 16.5762 7.86582C16.1539 7.44805 15.4711 7.44355 15.0533 7.86582L10.067 12.8521L7.95566 10.7408C7.5334 10.3186 6.85059 10.3186 6.43281 10.7408C6.01504 11.1631 6.01055 11.8459 6.43281 12.2637L9.30781 15.1387C9.73008 15.5609 10.4129 15.5609 10.8307 15.1387L16.5762 9.38867Z" fill="currentColor" />
          </svg>
        </div>;

      case StepStatus.Active:
        return <ClockIcon className="w-6 h-6 text-orange-600" />;
      case StepStatus.Error:
        return (
          <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center">
            <svg className="w-4 h-4 text-white" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
        );
      default:
        return <div className="w-6 h-6 rounded-full border-2 border-gray-300" />;
    }
  };

  const getSubStepIcon = (status: StepStatus): JSX.Element => {
    switch (status) {
      case StepStatus.Completed:
        return (
          <div className="w-4 h-4">
            <svg className="text-primary-600" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        );
      case StepStatus.Active:
      case StepStatus.InProgress:
        return <div className="w-4 h-4 rounded-full border-2 border-orange-600" />;
      case StepStatus.NotStarted:
      default:
        return <div className="w-4 h-4 rounded-full border-2 border-gray-300" />;
    }
  };

  const getSectionStyles = (status: StepStatus): string => {
    switch (status) {
      case StepStatus.Active:
      case StepStatus.InProgress:
        return 'border-orange-500';
      case StepStatus.Error:
        return 'border-red-300 bg-red-50';
      case StepStatus.Completed:
        return 'border-primary-600';
      default:
        return 'border-gray-200';
    }
  };

  const getDebugInfo = (step: Step) => {
    const stepStatus = step.status || 'undefined';
    const subStepsStatus = step.subSteps?.map(sub => ({
      name: sub.subStep,
      status: sub.status || 'undefined'
    }));

    return (
      <div className="text-xs bg-gray-100 p-2 mt-2 rounded">
        <div>Main step status: {stepStatus}</div>
        {subStepsStatus && subStepsStatus.length > 0 && (
          <div>
            <div>Substeps status:</div>
            {subStepsStatus.map((sub, idx) => (
              <div key={idx} className="ml-2">
                - {sub.name}: {sub.status}
              </div>
            ))}
          </div>
        )}
        <div className="mt-1 text-blue-600">
          Is Fully Complete: {isStepFullyComplete(step) ? 'Yes' : 'No'}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-3 max-w-md">
      {processedSteps.map((step, stepIndex) => {
        const hasSubSteps = step.subSteps && step.subSteps.length > 0;
        const { stepIndex: firstIncompleteStepIndex } = findFirstIncompleteStep(retirementSteps);

        // Only show as active if this is the first incomplete step
        const isCurrentIncompleteStep = stepIndex === firstIncompleteStepIndex;

        // Only show resume button on the first incomplete step
        const shouldShowResumeButton = isCurrentIncompleteStep;

        // Determine the overall status based on substeps
        const stepOverallStatus = isStepFullyComplete(step) ?
          StepStatus.Completed :
          (isCurrentIncompleteStep ? StepStatus.Active : StepStatus.NotStarted);

        const isExpanded = expandedSections[step.slug] || isCurrentIncompleteStep;
        return (
          <div
            key={step.slug}
            className={`border rounded-lg overflow-hidden bg-white shadow-lg ${getSectionStyles(stepOverallStatus || StepStatus.NotStarted)}`}
          >
            <button
              onClick={() => hasSubSteps && toggleSection(step.slug)}
              className="w-full px-6 py-4 flex items-center justify-between text-left"
              disabled={!hasSubSteps}
            >
              <div className="flex items-center space-x-3">
                {getStatusIcon(step)}
                <span className={`text-lg ${stepOverallStatus === StepStatus.Active ? 'text-gray-500 font-medium' :
                  stepOverallStatus === StepStatus.Completed ? 'text-primary-600' : 'text-gray-900'
                  }`}>
                  {step.step}
                </span>
              </div>
              {hasSubSteps && (
                <ChevronDown
                  className={`w-5 h-5 text-gray-400 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                />
              )}
            </button>

            {(isExpanded && hasSubSteps) && (
              <div className="border-t border-gray-200 bg-white px-6 py-3">
                {step.subSteps.map((subStep) => {
                  const isIncompleteSubStep = !subStep.status || subStep.status !== StepStatus.Completed;
                  return (
                    <div key={subStep.slug} className="py-2">
                      <div className="flex items-center space-x-3 pl-9">
                        {getSubStepIcon(subStep.status || StepStatus.NotStarted)}
                        <span className={`text-gray-700 ${isIncompleteSubStep ? 'font-medium' : ''}`}>
                          {subStep.subStep}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Show "Pick Up Where You Left Off" button */}
            {shouldShowResumeButton && (
              <div className="border-t border-gray-200 bg-white px-6 py-3">
                <Link
                  to={(() => {
                    const { stepIndex: firstIncompleteStepIndex, subStepIndex } = findFirstIncompleteStep(retirementSteps);
                    if (hasSubSteps && subStepIndex !== null) {
                      return `${step.slug}/${step.subSteps[subStepIndex].slug}`;
                    }
                    return step.slug;
                  })()}
                  className="block w-full py-3 px-4 text-center text-white hover:text-white bg-orange-500 hover:bg-orange-600 rounded-full transition-colors"
                  onClick={async (e: any) => await validateRetirementDate(e)}
                >
                  Pick Up Where You Left Off
                </Link>
              </div>
            )}

            {/* optional debug info */}
            {/* {getDebugInfo(step)} */}
          </div>
        );
      })}
    </div>
  );
});

export default RetirementOverview;